// Eskom PARCC — one shared field differ (PURE: no React, no Firebase).
//
// The only real old→new diff in the codebase was `diffRow` in the bulk-update
// matcher (matchBulkUpdate.ts), rendered in the bulk preview but dropped at the
// audit-persistence boundary — only the field LABELS reached the note, never the
// from/to. This generalises that one differ so every write path (Manage edit,
// working-paper edit, bulk update, single-finding edit) records WHAT changed in
// the same shape and the audit trail can render `Label: from → to`.
//
// `show()` and `same()` are moved here verbatim from matchBulkUpdate; `diffRow`
// now delegates to `diffFields`, so there is exactly one definition of "what
// counts as a change" and one of "how a value renders".
import type { BulkUpdateColumn } from './importColumns';

/** One field's before/after, ready to render or persist. */
export interface FieldChange {
  /** The object key that changed (e.g. `complianceLevel`). */
  field: string;
  /** Human label for the field (e.g. "Compliance level"). */
  label: string;
  /** The value before the write, rendered for display ('—' when unset). */
  from: string;
  /** The value the write would set. */
  to: string;
}

/** Render a stored value for a diff. blank → '—', boolean → Yes/No, number → en-ZA. */
export function show(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return v.toLocaleString('en-ZA');
  return String(v);
}

/** Canonical comparison: blank ≡ null ≡ absent; numbers numerically. */
export function same(a: unknown, b: unknown): boolean {
  const canon = (v: unknown): unknown => {
    if (v === undefined || v === null) return null;
    if (typeof v === 'string') return v.trim() === '' ? null : v.trim();
    return v;
  };
  const ca = canon(a);
  const cb = canon(b);
  if (typeof ca === 'number' || typeof cb === 'number') {
    const na = ca === null ? null : Number(ca);
    const nb = cb === null ? null : Number(cb);
    return na === nb;
  }
  return ca === cb;
}

/**
 * Human labels for the transaction whitelist columns — the single source shared
 * by the bulk preview and the audit trail. Covers every editable transaction
 * field (period, complianceLevel, complianceLevelOverride, reviewStatus,
 * reviewed, gapsStatus, …). Re-exported by matchBulkUpdate as `COLUMN_LABELS`.
 */
export const TRANSACTION_FIELD_LABELS: Record<BulkUpdateColumn, string> = {
  dataset: 'Dataset',
  period: 'Period',
  division: 'Division',
  supplier: 'Supplier',
  csdNumber: 'CSD Number',
  value: 'Value',
  valueDisplay: 'Value (display)',
  mechanism: 'Mechanism',
  link: 'OpenText link',
  ou: 'OU',
  contractNumber: 'Contract Number',
  cirNumber: 'CIR Number',
  linkProvided: 'Link provided',
  linkIssue: 'Issue with links',
  panel: 'Panel release',
  bu: 'BU',
  briefDescription: 'Brief description',
  buyer: 'Buyer',
  prNumber: 'PR number',
  procurementManager: 'Procurement manager',
  endUser: 'End-user',
  selfDeclaration: 'Self-declaration',
  initialFindings: 'Initial findings',
  updatedFindings: 'Updated findings',
  findingText: 'Finding text',
  complianceLevel: 'Compliance level',
  complianceLevelOverride: 'Compliance level (override)',
  reviewStatus: 'Review status',
  reviewed: 'Reviewed',
  gapsStatus: 'Gaps status',
};

/**
 * Diff `after` against `before`, one entry per genuinely-changed labelled field.
 *
 * Iterates the keys of `after` (the incoming change set) in their own order, and
 * for each: skips keys in `opts.skip`; skips `undefined` (absent from the write);
 * skips keys with no `labels` entry (so internal ids never surface); and emits a
 * change only when `same()` says the value actually moved. Blank ≡ null ≡ absent.
 */
export function diffFields(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  labels: Record<string, string>,
  opts?: { skip?: readonly string[] },
): FieldChange[] {
  const skip = new Set(opts?.skip ?? []);
  const changes: FieldChange[] = [];
  for (const [field, next] of Object.entries(after)) {
    if (skip.has(field)) continue;
    if (next === undefined) continue;
    const label = labels[field];
    if (label === undefined) continue;
    if (same(before[field], next)) continue;
    changes.push({ field, label, from: show(before[field]), to: show(next) });
  }
  return changes;
}

/**
 * The audit-note convention bulk update already uses: the changed field labels,
 * comma-joined — e.g. "Period, Compliance level, Reviewed". Keeps the old and
 * new audit notes reading alike.
 */
export function changesNote(changes: readonly FieldChange[]): string {
  return changes.map((c) => c.label).join(', ');
}
